import { Fragment, useCallback, useEffect, useState } from 'react'
import { EntityRef } from '../../components/EntityRef'
import { InlineAlert } from '../../components/InlineAlert'
import { StatusPill, type StatusPillVariant } from '../../components/StatusPill'
import { getFiscalYear } from '../../api/fiscalYears'
import { getProduction, listPersonnel, listSpendLineAdjustments, listSpendLines, listVendors } from '../../api/film'
import { ApiError } from '../../api/problem'
import { label } from '../../lib/labels'
import type { ProductionFYView, QualifiedSpendLineView, SpendAdjustmentView } from '../../types/film'

export interface SpendPanelProps {
  productionId: string
}

const METHOD_LABEL: Record<string, string> = {
  manual: 'Manual',
  computed: 'Computed',
  redistributed: 'Redistributed',
  ai_accept: 'AI accept',
  unlock: 'Unlock',
}

const METHOD_VARIANT: Record<string, StatusPillVariant> = {
  manual: 'info',
  computed: 'neutral',
  redistributed: 'warning',
  ai_accept: 'success',
  unlock: 'muted',
}

function formatAmount(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount)
  } catch {
    return `${currency} ${amount.toFixed(2)}`
  }
}

function formatPct(pct: number | null): string {
  return pct !== null ? `${pct}%` : 'Not yet assessed'
}

/**
 * The read-only adjustment-history row (mirrors `SpendGrid.tsx`'s own `AdjustmentHistoryContent`
 * flyout, `GET /qualified-spend-lines/{id}/adjustments`, IB-155/IB-53b/ADR-0041) — this Case
 * workspace tab is a summary view, not the editing grid (`SpendGrid` on the Production detail
 * screen owns the one guarded write path), so history here is inline-expand, never a Popover.
 */
function AdjustmentHistoryRow({ spendLineId }: { spendLineId: string }) {
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading')
  const [records, setRecords] = useState<SpendAdjustmentView[]>([])
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(() => {
    setState('loading')
    setError(null)
    listSpendLineAdjustments(spendLineId)
      .then((data) => {
        setRecords(data)
        setState('ready')
      })
      .catch((err) => {
        setError(err instanceof ApiError ? (err.problem.detail ?? err.message) : 'Could not load adjustment history.')
        setState('error')
      })
  }, [spendLineId])

  useEffect(() => {
    load()
  }, [load])

  if (state === 'loading') {
    return (
      <p style={{ margin: 0, color: 'var(--iw-text-muted)' }} role="status">
        Loading adjustment history…
      </p>
    )
  }

  if (state === 'error') {
    return (
      <InlineAlert
        severity="error"
        action={
          <button type="button" className="iw-btn iw-btn--sm" onClick={load}>
            Retry
          </button>
        }
      >
        {error}
      </InlineAlert>
    )
  }

  if (records.length === 0) {
    return <p style={{ margin: 0, color: 'var(--iw-text-muted)' }}>No adjustments recorded yet.</p>
  }

  return (
    <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-2)' }}>
      {records.map((record, index) => (
        <li key={`${record.appliedAt}-${index}`} style={{ borderBottom: '1px solid var(--iw-border)', paddingBottom: 'var(--iw-space-2)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--iw-space-2)', flexWrap: 'wrap' }}>
            <StatusPill variant={METHOD_VARIANT[record.method] ?? 'neutral'} label={METHOD_LABEL[record.method] ?? record.method} />
            <strong>
              {record.priorValue !== null ? `${record.priorValue}%` : '—'} → {record.newValue}%
            </strong>
          </div>
          <p style={{ margin: '4px 0 0' }}>{record.reason}</p>
          <p style={{ margin: '4px 0 0', color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)' }}>
            {record.channel} · {new Date(record.appliedAt).toLocaleString()}
          </p>
        </li>
      ))}
    </ul>
  )
}

interface CategoryTotal {
  category: string
  amount: number
  currency: string
  pctOfTotal: number
}

/**
 * Category-subtotal breakdown (case-enrichment story) — groups the already-loaded spend rows by
 * `category` and shows each subtotal's share of the qualified-spend total. Pure client-side
 * arithmetic over data already on screen, never a second write path, never a re-derivation of a
 * `@GuardedField`.
 */
function categoryTotals(rows: QualifiedSpendLineView[]): CategoryTotal[] {
  const grandTotal = rows.reduce((sum, r) => sum + r.amount, 0)
  const byCategory = new Map<string, { amount: number; currency: string }>()
  for (const row of rows) {
    const existing = byCategory.get(row.category)
    byCategory.set(row.category, { amount: (existing?.amount ?? 0) + row.amount, currency: row.currency })
  }
  return Array.from(byCategory.entries()).map(([category, { amount, currency }]) => ({
    category,
    amount,
    currency,
    pctOfTotal: grandTotal > 0 ? (amount / grandTotal) * 100 : 0,
  }))
}

/**
 * The fiscal-year roll-up summary (case-enrichment story) — the same `ProductionFYView` figures
 * `ProductionDetailScreen`'s own "Fiscal-year roll-up" section shows (simple-average vs.
 * cost-weighted `qualifyingPct`, provisional/final credit), rendered here read-only (no
 * "Use this instead" switch — that write path stays on the Production detail screen's own
 * `SpendGrid`/roll-up section). `null` renders "Not yet assessed", never a fabricated figure.
 */
function RollupSummary({ productionFys }: { productionFys: ProductionFYView[] }) {
  if (productionFys.length === 0) return null
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-2)' }}>
      {productionFys.map((fy) => (
        <div key={fy.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--iw-space-4)', flexWrap: 'wrap' }}>
          <span style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)' }}>Qualified spend total</span>
          <strong>
            {fy.qualifiedSpendTotal !== null ? formatAmount(fy.qualifiedSpendTotal, fy.qualifiedSpendTotalCurrency ?? 'USD') : 'Not yet assessed'}
          </strong>
          <span style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)' }}>Simple average</span>
          <strong>{formatPct(fy.qualifyingPctAvg)}</strong>
          <span style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)' }}>Cost-weighted</span>
          <strong>{formatPct(fy.qualifyingPctEffective)}</strong>
          <StatusPill
            variant="info"
            label={`Authoritative: ${fy.authoritativeRollup === 'cost_weighted' ? 'Cost-weighted' : 'Simple average'}`}
          />
          <span style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)' }}>Provisional credit</span>
          <strong>
            {fy.creditProvisional !== null ? formatAmount(fy.creditProvisional, fy.qualifiedSpendTotalCurrency ?? 'USD') : 'Not yet assessed'}
          </strong>
        </div>
      ))}
    </div>
  )
}

/**
 * The Case-workspace Spend tab (GA demo v4, film cases only; further enriched by the
 * case-enrichment story) — a read-only summary over `GET /productions/{productionId}/spend-lines`
 * (the same data `SpendGrid` edits on the Production detail screen), each row's guarded
 * `qualifyingPct` adjustment history, a category-subtotal breakdown, and the fiscal-year roll-up
 * (`ProductionFYView`, via the already-published single-call `GET /productions/{id}`). No write
 * path lives here — editing stays on the Production detail screen's `SpendGrid`.
 */
export function SpendPanel({ productionId }: SpendPanelProps) {
  const [rows, setRows] = useState<QualifiedSpendLineView[]>([])
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading')
  const [error, setError] = useState<string | null>(null)
  const [historyOpenId, setHistoryOpenId] = useState<string | null>(null)
  const [productionFys, setProductionFys] = useState<ProductionFYView[]>([])
  // Best-effort Vendor/Personnel/FiscalYear name resolution (CLAUDE.md UX-quality pass — never a
  // bare UUID) over already-published, unpaginated `GET /vendors`/`GET /productions/{id}/personnel`
  // reads `ProductionDetailScreen` already uses, plus `GET /fiscal-years/{id}` per distinct id on
  // this production's rows. A failed lookup degrades to an empty map — the row still renders via
  // `EntityRef`'s own generic "<Kind> <shortId>" fallback, never a crash.
  const [vendorNameById, setVendorNameById] = useState<Map<string, string>>(new Map())
  const [personnelNameById, setPersonnelNameById] = useState<Map<string, string>>(new Map())
  const [fiscalYearLabelById, setFiscalYearLabelById] = useState<Map<string, string>>(new Map())

  const load = useCallback(() => {
    setState('loading')
    setError(null)
    listSpendLines(productionId)
      .then((data) => {
        setRows(data)
        setState('ready')
        const distinctFyIds = Array.from(new Set(data.map((r) => r.fiscalYearId).filter(Boolean)))
        Promise.all(distinctFyIds.map((fyId) => getFiscalYear(fyId).catch(() => null))).then((years) => {
          setFiscalYearLabelById(
            new Map(years.filter((y): y is NonNullable<typeof y> => y !== null).map((y) => [y.id, y.label])),
          )
        })
      })
      .catch((err) => {
        setError(err instanceof ApiError ? (err.problem.detail ?? err.message) : 'Could not load spend lines.')
        setState('error')
      })
    Promise.all([listVendors().catch(() => []), listPersonnel(productionId).catch(() => [])]).then(
      ([vendors, personnel]) => {
        setVendorNameById(new Map(vendors.map((v) => [v.id, v.name])))
        setPersonnelNameById(new Map(personnel.map((p) => [p.id, p.personRef])))
      },
    )
    getProduction(productionId)
      .then((production) => setProductionFys(production.productionFys ?? []))
      .catch(() => setProductionFys([]))
  }, [productionId])

  useEffect(() => {
    load()
  }, [load])

  if (state === 'loading') {
    return <p style={{ color: 'var(--iw-text-muted)' }}>Loading spend lines…</p>
  }

  if (state === 'error') {
    return (
      <InlineAlert
        severity="error"
        action={
          <button type="button" className="iw-btn iw-btn--sm" onClick={load}>
            Try again
          </button>
        }
      >
        {error}
      </InlineAlert>
    )
  }

  if (rows.length === 0) {
    return <p style={{ color: 'var(--iw-text-muted)' }}>No qualified spend lines recorded yet for this production.</p>
  }

  const totals = categoryTotals(rows)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-4)' }}>
      <section
        style={{
          padding: 'var(--iw-space-4)',
          background: 'var(--iw-surface)',
          border: '1px solid var(--iw-border)',
          borderRadius: 'var(--iw-radius-lg)',
        }}
      >
        <h3 style={{ margin: '0 0 var(--iw-space-3)', fontSize: 'var(--iw-size-md)' }}>Fiscal-year roll-up</h3>
        <RollupSummary productionFys={productionFys} />
      </section>

      <section
        style={{
          padding: 'var(--iw-space-4)',
          background: 'var(--iw-surface)',
          border: '1px solid var(--iw-border)',
          borderRadius: 'var(--iw-radius-lg)',
        }}
      >
        <h3 style={{ margin: '0 0 var(--iw-space-3)', fontSize: 'var(--iw-size-md)' }}>Category breakdown</h3>
        <table className="iw-table" aria-label="Spend category breakdown">
          <thead>
            <tr>
              <th scope="col">Category</th>
              <th scope="col">Subtotal</th>
              <th scope="col">% of total</th>
            </tr>
          </thead>
          <tbody>
            {totals.map((t) => (
              <tr key={t.category}>
                <td>{label('spendCategory', t.category)}</td>
                <td>{formatAmount(t.amount, t.currency)}</td>
                <td>{t.pctOfTotal.toFixed(1)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <table className="iw-table" aria-label="Qualified spend lines">
        <thead>
          <tr>
            <th scope="col">Category</th>
            <th scope="col">Amount</th>
            <th scope="col">Qualifying %</th>
            <th scope="col">Fiscal year</th>
            <th scope="col">Vendor</th>
            <th scope="col">Personnel</th>
            <th scope="col">History</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const expanded = historyOpenId === row.id
            return (
              <Fragment key={row.id}>
                <tr>
                  <td>{label('spendCategory', row.category)}</td>
                  <td>{formatAmount(row.amount, row.currency)}</td>
                  <td>{row.qualifyingPct !== null ? `${row.qualifyingPct}%` : '—'}</td>
                  <td style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)' }}>
                    <EntityRef kind="fiscalYear" id={row.fiscalYearId} name={fiscalYearLabelById.get(row.fiscalYearId)} />
                  </td>
                  <td style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)' }}>
                    <EntityRef kind="vendor" id={row.vendorId} name={row.vendorId ? vendorNameById.get(row.vendorId) : null} />
                  </td>
                  <td style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)' }}>
                    <EntityRef
                      kind="record"
                      id={row.personnelId}
                      name={row.personnelId ? personnelNameById.get(row.personnelId) : null}
                    />
                  </td>
                  <td>
                    <button
                      type="button"
                      className="iw-btn iw-btn--sm"
                      aria-expanded={expanded}
                      aria-label={`Adjustment history for ${label('spendCategory', row.category)}`}
                      onClick={() => setHistoryOpenId(expanded ? null : row.id)}
                    >
                      {expanded ? 'Hide history' : 'History'}
                    </button>
                  </td>
                </tr>
                {expanded ? (
                  <tr>
                    <td colSpan={7}>
                      <AdjustmentHistoryRow spendLineId={row.id} />
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
